<template>
  <section
    :class="getComponentClass(blok, config)"
    @mouseover="pauseProgressRing()"
    @mouseleave="startProgressRing()"
  >
    <div :class="config.className">
      <template v-if="blok.sectionHeader && blok.sectionHeader[0]">
        <component
          :is="'m-sectionHeader' | dashify"
          :blok="blok.sectionHeader[0]"
          :theme="config.theme"
        />
      </template>
      <button
        v-if="blok.autoplay"
        type="button"
        class="slide-projector__refresh"
        @click="triggerMove = true"
      >
        <div class="icon icon--refresh"></div>
      </button>
      <a-progress-ring
        v-if="blok.autoplay"
        slot="control"
        :progress="progress"
        radius="28"
        stroke="2"
      ></a-progress-ring>
      <transition name="fade">
        <div
          v-if="showContent && config.template === 'peopleList'"
          class="row slide-projector__team-list"
        >
          <div
            v-for="person in currentDisplayed"
            :key="person.id"
            class="slide-projector__item col-6 col-md-4 col-lg-2"
          >
            <component
              :is="'a_imageSource' | dashify"
              :blok="person.content"
              :image-url="person.content.image"
              :alt="person.content.name"
              modifier-class="slide-projector__image"
            />
            <p class="slide-projector__name">{{ person.content.name }}</p>
            <p class="slide-projector__title">{{ person.content.title }}</p>
          </div>
        </div>
        <component
          :is="'m-slide' | dashify"
          v-if="
            config.template === 'slide' && currentDisplayed[0] && showContent
          "
          :slide="currentDisplayed[0].content"
        />
      </transition>
    </div>
  </section>
</template>

<script>
import AProgressRing from '@/components/atoms/a-svgs/a-svg-progress'

export default {
  components: {
    AProgressRing,
  },
  props: {
    blok: {
      type: Object,
      default: () => {},
    },
    visible: {
      type: Boolean,
      default: () => false,
    },
    visibleSectionIndex: {
      type: Number,
      default: () => 1000,
    },
  },
  data: () => ({
    slideDatas: [],
    currentDisplayed: [],
    remainingStock: [],
    triggerMove: false,
    config: {},
    showContent: true,
    active: true,
    interval: false,
    progress: 0,
  }),
  watch: {
    triggerMove(newValue) {
      if (newValue === true) {
        this.showContent = false
        this.pauseProgressRing()

        setTimeout(() => {
          this.getPeopleSlideDatas(this.slideDatas)

          this.startProgressRing()

          this.showContent = true
        }, 400)
      }
    },
    visible(visible) {
      if (visible) {
        this.$store.commit(
          'globals/setVisibleSectionName',
          this.blok.breadcrumb
        )
        this.$store.commit(
          'globals/setVisibleSectionIndex',
          this.visibleSectionIndex
        )
      }
    },
  },
  mounted() {
    this.setConfig(this.blok.variation)

    this.$storyapi
      .get('cdn/stories', {
        starts_with: `${this.blok.variation}/`,
        is_startpage: '0',
        per_page: 100,
        cv: this.$store.state.cacheVersion,
      })
      .then((res) => {
        this.slideDatas = res.data.stories
        if (this.blok.autoplay) {
          this.triggerMove = true
          this.startProgressRing()
        } else {
          this.getPeopleSlideDatas(this.slideDatas)
        }
      })
      .catch((res) => {
        // eslint-disable-next-line no-console
        console.error('Slide Projector - Failed to load resource', res)
      })
  },
  methods: {
    getComponentClass(blok, config) {
      const height = blok.isHScreen ? ' h-screen' : 'h-default'
      return `slide-projector ${config.theme} ${height}`
    },
    startProgressRing() {
      if (!this.blok.autoplay) {
        return
      }

      this.active = true
      this.progress = 0
      clearInterval(this.interval)

      this.interval = setInterval(() => {
        this.progress += 1
        if (this.progress === 100) {
          this.triggerMove = true
          clearInterval(this.interval)
        }
      }, this.config.speed / 100)
    },
    pauseProgressRing() {
      this.active = false
      clearInterval(this.interval)
    },
    getPeopleSlideDatas() {
      // if slideDatas is empty, stop here
      if (!this.slideDatas) {
        return
      }

      // if remainingStock is empty fill it from slideDatas, otherwise use previous set
      if (!this.remainingStock.length) {
        this.remainingStock = Object.assign([], this.slideDatas)
      }

      let dataLen = this.remainingStock.length
      // save previous so we dont lose that data
      const previousStock = this.currentDisplayed.length
        ? Object.assign([], this.currentDisplayed)
        : []
      // set length of loop, it cant be more than available items
      let pickLen =
        this.config.pickAmount > dataLen ? dataLen : this.config.pickAmount
      // reset currentDisplayed
      this.currentDisplayed = []

      // loop and pick certain amount of items randomly
      // eslint-disable-next-line no-plusplus
      while (pickLen--) {
        const pickKey = Math.floor(Math.random() * dataLen)

        // push picked ones into currentDisplayed. Pick from remainingStock, so we avoid duplicates.
        this.currentDisplayed.push(this.remainingStock[pickKey])
        // remove picked ones from remainingStock
        this.remainingStock.splice(pickKey, 1)
        // update dataLen (removing item changed it)
        dataLen = this.remainingStock.length
      }

      // merge previous back into remaining, so that data will be available next time
      this.remainingStock = this.remainingStock.concat(previousStock)

      // set triggerMove false so we can trigger next time
      if (this.blok.autoplay) {
        this.triggerMove = false
      }
    },
    setConfig(variation) {
      switch (variation) {
        case 'teams':
          this.config = {
            template: 'peopleList',
            theme: 'theme-dark',
            pickAmount: 6,
            speed: 5000,
            className: 'container slide-projector__team',
          }
          break
        case 'facts':
          this.config = {
            template: 'slide',
            theme: 'theme-white',
            pickAmount: 1,
            speed: 5000,
            className: 'container slide-projector__slide',
          }
          break
        default:
          this.config = {
            template: '',
            className: '',
          }
      }
    },
  },
}
</script>

<style lang="scss">
@import '../../assets/scss/settings';

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-projector {
  &.theme-dark {
    background: $black;

    .progress-ring__circle {
      stroke: $white;
    }

    .slide-projector__refresh {
      color: $white;

      &:focus {
        color: $white;
      }

      &:active,
      &:hover {
        color: $oslo-gray;
      }
    }
  }

  &.theme-light {
    .progress-ring__circle {
      stroke: $oslo-gray;
    }

    .slide-projector__refresh {
      color: $oslo-gray;

      &:focus {
        color: $oslo-gray;
      }

      &:active,
      &:hover {
        color: $mine-shaft;
      }
    }
  }

  .progress-ring {
    position: absolute;
    top: calc(100vh - 100px);
    right: calc(50% - 27px);
  }

  &__refresh {
    background-color: transparent;
    border: 1px transparent;
    border-radius: 50%;
    cursor: pointer;
    height: 54px;
    padding: 0;
    position: absolute;
    top: calc(100vh - 100px);
    right: calc(50% - 27px);
    transition: $transition-base;
    width: 54px;
    z-index: 100;
    outline: none;

    .icon--refresh {
      &::before {
        @include font-size(28, rem-v(28));

        display: block;
      }
    }
  }

  &__team {
    position: relative;
    .section-header {
      padding-top: 45px;
    }
  }

  &__slide {
    position: relative;
    z-index: 2;

    .section-header {
      position: absolute;
      top: 35px;
      width: 100%;
      background: transparent;
    }

    .media-asset__image {
      text-align: center;
      img {
        max-width: 100%;
      }
    }
  }

  &__team-list {
    padding: spacer(3) 0 spacer(6);
  }

  &__item {
    margin-top: spacer();
  }

  &__image {
    max-width: 100%;
  }

  &__name {
    margin-top: 28px;
  }

  &__title {
    @include font-size(12);

    margin-top: 0;
  }

  .slide__content .slide__content-left {
    margin-top: spacer(8);
  }

  .slide__content .slide__content-right {
    margin-bottom: spacer(8);
    margin-top: spacer(2);
  }
}

@include media-breakpoint-up(md) {
  .slide-projector__team-list {
    padding: spacer(13) 0 spacer(13);
  }

  .slide-projector .slide__content .slide__content-left {
    margin-top: spacer(15);
  }

  .slide-projector .slide__content .slide__content-right {
    margin-top: spacer(10);
  }

  .slide-projector .slide-projector__refresh {
    top: 30px;
    right: 31px;
  }

  .slide-projector .progress-ring {
    right: spacer(2);
    top: 30px;
  }

  .slide__content .slide__content-right {
    margin-bottom: 0;
  }
}
</style>
